import React, { useState, useEffect, useRef } from 'react';
import './Sidebar.css';

interface SidebarProps {
  dateRange?: [Date, Date];
  selectedDate?: Date;
  filterType: 'single' | 'range';
  onFilterTypeChange: (type: 'single' | 'range') => void;
  onDateRangeChange: (range: Date | [Date, Date]) => void;
  selectedCategories: string[];
  onCategoryChange: (categories: string[]) => void;
  categories: string[];
  selectedPriorities: string[];
  onPriorityChange: (priorities: string[]) => void;
  priorities: string[];
  showCameras: boolean;
  onShowCamerasChange: (show: boolean) => void;
  showHeatmap: boolean;
  onShowHeatmapChange: (show: boolean) => void;
  withinCameraRange: boolean;
  onWithinRangeChange: (withinRange: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  dateRange,
  selectedDate,
  filterType,
  onFilterTypeChange,
  onDateRangeChange,
  selectedCategories,
  onCategoryChange,
  categories,
  selectedPriorities,
  onPriorityChange,
  priorities,
  showCameras,
  onShowCamerasChange,
  showHeatmap,
  onShowHeatmapChange,
  withinCameraRange,
  onWithinRangeChange
}) => {
  // Use filterType prop to initialize and sync dateType
  const [dateType, setDateType] = useState<'single' | 'range'>(filterType);
  const [showRangeBetaNotice, setShowRangeBetaNotice] = useState(false);
  const rangeBetaTimeoutRef = useRef<number | null>(null);

  // Sync internal state with props
  useEffect(() => {
    setDateType(filterType);
  }, [filterType]);

  // Ensure singleDate and dateRange states are synced with props
  useEffect(() => {
    if (selectedDate) {
      setSingleDate(selectedDate);
    }
    if (dateRange) {
      setStartDate(dateRange[0]);
      setEndDate(dateRange[1]);
    }
  }, [selectedDate, dateRange]);

  useEffect(() => {
    return () => {
      if (rangeBetaTimeoutRef.current) {
        window.clearTimeout(rangeBetaTimeoutRef.current);
        rangeBetaTimeoutRef.current = null;
      }
    };
  }, []);

  const showRangeBeta = () => {
    setShowRangeBetaNotice(true);
    if (rangeBetaTimeoutRef.current) {
      window.clearTimeout(rangeBetaTimeoutRef.current);
    }
    rangeBetaTimeoutRef.current = window.setTimeout(() => {
      setShowRangeBetaNotice(false);
      rangeBetaTimeoutRef.current = null;
    }, 5000);
  };

  const [singleDate, setSingleDate] = useState<Date | undefined>(selectedDate);
  const [startDate, setStartDate] = useState<Date | undefined>(dateRange?.[0]);
  const [endDate, setEndDate] = useState<Date | undefined>(dateRange?.[1]);

  const handleSingleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Get the raw date string from the input
    const dateString = e.target.value; // Format: YYYY-MM-DD
    console.log(`Selected date from calendar: ${dateString}`);

    // Create a date at noon on the selected day to avoid timezone issues
    const selectedDate = new Date(`${dateString}T12:00:00`);
    console.log(`Created Date object: ${selectedDate.toISOString()}`);

    // Update internal state
    setSingleDate(selectedDate);

    // Pass this date to the parent component
    onDateRangeChange(selectedDate);
  };

  const formatDateForInput = (date: Date | undefined) => {
    if (!date) {
      // Default to today if no date is provided
      date = new Date();
    }

    // Extract year, month, and day directly from the date
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    // Format date as YYYY-MM-DD for input element
    return `${year}-${month}-${day}`;
  };

  const handleDateRangeChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'start' | 'end') => {
    const dateString = e.target.value;
    // Use local noon to avoid timezone shifts; keep consistent with single-day selection
    const newDate = new Date(`${dateString}T12:00:00`);
    console.log(`Selected ${type} date: ${dateString} -> ${newDate.toISOString()}`);

    if (type === 'start') {
      setStartDate(newDate);
      if (endDate) {
        onDateRangeChange([newDate, endDate]);
      }
    } else {
      setEndDate(newDate);
      if (startDate) {
        onDateRangeChange([startDate, newDate]);
      }
    }
  };

  const handleDateTypeChange = (type: 'single' | 'range') => {
    setDateType(type);
    onFilterTypeChange(type);
  };

  const handleCategoryToggle = (category: string) => {
    if (category === 'All Categories') {
      if (selectedCategories.includes('All Categories')) {
        onCategoryChange([]);
      } else {
        onCategoryChange(['All Categories']);
      }
    } else {
      let newCategories = [...selectedCategories];

      if (selectedCategories.includes(category)) {
        newCategories = newCategories.filter(c => c !== category);
      } else {
        newCategories.push(category);
        newCategories = newCategories.filter(c => c !== 'All Categories');
      }

      if (newCategories.length === 0) {
        newCategories = ['All Categories'];
      }

      onCategoryChange(newCategories);
    }
  };

  const handlePriorityToggle = (priority: string) => {
    if (priority === 'All Priorities') {
      if (selectedPriorities.includes('All Priorities')) {
        onPriorityChange([]);
      } else {
        onPriorityChange(['All Priorities']);
      }
    } else {
      let newPriorities = [...selectedPriorities];

      if (selectedPriorities.includes(priority)) {
        newPriorities = newPriorities.filter(p => p !== priority);
      } else {
        newPriorities.push(priority);
        newPriorities = newPriorities.filter(p => p !== 'All Priorities');
      }

      if (newPriorities.length === 0) {
        newPriorities = ['All Priorities'];
      }

      onPriorityChange(newPriorities);
    }
  };

  const applyFilters = () => {
    // Implementation of applyFilters
    // Already handled by onChange events
  };

  const resetFilters = () => {
    // Reset to today
    const today = new Date();

    if (dateType === 'single') {
      setSingleDate(today);
      onDateRangeChange(today);
    } else {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      setStartDate(weekAgo);
      setEndDate(today);
      onDateRangeChange([weekAgo, today]);
    }

    // Reset categories to 'All Categories'
    onCategoryChange(['All Categories']);

    // Reset priorities to 'All Priorities'
    onPriorityChange(['All Priorities']);
  };

  return (
    <div className="tactical-sidebar">
      <div className="tactical-module">
        <div className="module-header">DATE PARAMETERS</div>
        <div className="module-content">
          <div className="form-control">
            <label className="control-label">DATE RANGE</label>
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="singleDay"
                  name="dateType"
                  value="single"
                  checked={dateType === 'single'}
                  onChange={() => handleDateTypeChange('single')}
                />
                <label htmlFor="singleDay">SINGLE DAY</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="dateRange"
                  name="dateType"
                  value="range"
                  checked={dateType === 'range'}
                  onChange={() => handleDateTypeChange('range')}
                />
                <label htmlFor="dateRange">DATE RANGE</label>
              </div>
            </div>
          </div>

          {dateType === 'single' ? (
            <div className="form-control">
              <label className="control-label">DATE</label>
              <input
                type="date"
                className="date-input"
                value={formatDateForInput(singleDate)}
                onChange={handleSingleDateChange}
              />
            </div>
          ) : (
            <>
              <div className="form-control">
                <label className="control-label">START DATE</label>
                <input
                  type="date"
                  className="date-input"
                  value={formatDateForInput(startDate)}
                  onChange={(e) => handleDateRangeChange(e, 'start')}
                />
              </div>
              <div className="form-control">
                <label className="control-label">END DATE</label>
                <input
                  type="date"
                  className="date-input"
                  value={formatDateForInput(endDate)}
                  onChange={(e) => handleDateRangeChange(e, 'end')}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="tactical-module">
        <div className="module-header">INCIDENT FILTERS</div>
        <div className="module-content">
          {/* <div className="form-control">
            <label className="control-label">CATEGORIES</label>
            <div className="categories-container">
              <div className="category-checkbox">
                <input
                  type="checkbox"
                  id={`category-All Categories`}
                  checked={selectedCategories.includes('All Categories')}
                  onChange={() => handleCategoryToggle('All Categories')}
                />
                <label htmlFor={`category-All Categories`}>All Categories</label>
              </div>

              {categories.filter(cat => cat !== 'All Categories').map(category => (
                <div key={category} className="category-checkbox">
                  <input
                    type="checkbox"
                    id={`category-${category}`}
                    checked={selectedCategories.includes(category)}
                    onChange={() => handleCategoryToggle(category)}
                  />
                  <label htmlFor={`category-${category}`}>{category}</label>
                </div>
              ))}
            </div>
          </div> */}

          <div className="form-control">
            <label className="control-label">PRIORITY</label>
            <div className="priorities-container">
              <div className="priority-checkbox">
                <input
                  type="checkbox"
                  id={`priority-All Priorities`}
                  checked={selectedPriorities.includes('All Priorities')}
                  onChange={() => handlePriorityToggle('All Priorities')}
                />
                <label htmlFor={`priority-All Priorities`}>All Priorities</label>
              </div>

              {priorities.filter(pri => pri !== 'All Priorities').map(priority => (
                <div key={priority} className="priority-checkbox">
                  <input
                    type="checkbox"
                    id={`priority-${priority}`}
                    checked={selectedPriorities.includes(priority)}
                    onChange={() => handlePriorityToggle(priority)}
                  />
                  <label htmlFor={`priority-${priority}`}>{priority}</label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="tactical-module">
        <div className="module-header">MAP CONTROLS</div>
        <div className="module-content">
          <div className="form-control">
            <div className="toggle-group">
              {/* <div className="toggle-option">
                <input
                  type="checkbox"
                  id="showCameras"
                  className="toggle-input"
                  checked={showCameras}
                  onChange={(e) => onShowCamerasChange(e.target.checked)}
                />
                <label htmlFor="showCameras" className="toggle-label">
                  <span className="toggle-switch"></span>
                  <span className="toggle-text">SHOW CAMERAS</span>
                </label>
              </div> */}

              <div className="toggle-option">
                <input
                  type="checkbox"
                  id="showHeatmap"
                  className="toggle-input"
                  checked={showHeatmap}
                  onChange={(e) => onShowHeatmapChange(e.target.checked)}
                />
                <label htmlFor="showHeatmap" className="toggle-label">
                  <span className="toggle-switch"></span>
                  <span className="toggle-text">ENABLE HEATMAP</span>
                </label>
              </div>

              <div className="toggle-option">
                <input
                  type="checkbox"
                  id="withinRange"
                  className="toggle-input"
                  checked={withinCameraRange}
                  aria-disabled="true"
                  onChange={(e) => {
                    e.preventDefault();
                    showRangeBeta();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      showRangeBeta();
                    }
                  }}
                />
                <label
                  htmlFor="withinRange"
                  className="toggle-label"
                  onClick={(e) => {
                    e.preventDefault();
                    showRangeBeta();
                  }}
                  title="Range is not available in the beta"
                  aria-disabled="true"
                >
                  <span className="toggle-switch"></span>
                  <span className="toggle-text">WITHIN RANGE (1000 FT)</span>
                </label>
                {showRangeBetaNotice && (
                  <div className="within-range-beta-notice" role="status">
                    <strong>Range</strong>
                    <p>Not part of the beta. Access is limited to law enforcement.</p>
                    <button
                      className="within-range-beta-dismiss"
                      aria-label="Dismiss notice"
                      onClick={() => setShowRangeBetaNotice(false)}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tactical-actions">
        <button className="action-button primary" onClick={applyFilters}>
          APPLY FILTERS
        </button>
        <button className="action-button secondary" onClick={resetFilters}>
          RESET
        </button>
      </div>
    </div>
  );
};

export default Sidebar; 